import type { TravelBallSport } from '../../../types/travelBallAdmin';
import { isFreshTravelBallSignup } from './tbSignupStorage';

/**
 * Keeps the travel ball PROGRAM signup form across a refresh of the verify step, and across tabs.
 * localStorage (not sessionStorage, because the verification link opens a second tab that must
 * find the same session), bound to the Firebase account's address so a stored blob for someone else
 * is ignored, cleared on sign-out from `AuthContext`, and **never holds the password**. Every access
 * is wrapped: blocked site data degrades to "no resume", never to a broken page.
 *
 * Separate from the family signup's storage so a user who starts one, cancels and starts the other
 * does not see the wrong form resumed. Both expire after `TRAVEL_BALL_SIGNUP_MAX_AGE_MS` (see
 * `tbSignupStorage.ts`).
 */

const STORAGE_KEY = 'rosterpilot.travelBallProgramSignup';

export interface StoredTravelBallProgramSignup {
  step: 'verify';
  email: string;
  firstName: string;
  lastName: string;
  sport: TravelBallSport;
  programName: string;
  /**
   * The form cannot be submitted without agreeing to the Terms of Service and Privacy Policy, so a
   * stored signup always carries it. A blob without it (from a build before the checkbox) is
   * dropped, so the coach goes back through the form and agrees.
   */
  acceptedTerms: true;
}

const isSport = (value: unknown): value is TravelBallSport =>
  value === 'baseball' || value === 'softball';

export const saveTravelBallProgramSignup = (state: StoredTravelBallProgramSignup): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...state, savedAt: Date.now() }));
  } catch {
    // Storage unavailable: a refresh will start over, which is safe.
  }
};

export const clearTravelBallProgramSignup = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
};

/** The stored signup, only when it belongs to `email`. */
export const loadTravelBallProgramSignup = (
  email: string | null | undefined,
): StoredTravelBallProgramSignup | null => {
  let raw: string | null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as
      | (Partial<Record<keyof StoredTravelBallProgramSignup, unknown>> & { savedAt?: unknown })
      | null;
    if (
      parsed?.step === 'verify' &&
      isFreshTravelBallSignup(parsed.savedAt) &&
      typeof parsed.email === 'string' &&
      typeof parsed.firstName === 'string' &&
      typeof parsed.lastName === 'string' &&
      isSport(parsed.sport) &&
      typeof parsed.programName === 'string' &&
      parsed.acceptedTerms === true &&
      email &&
      parsed.email.toLowerCase() === email.toLowerCase()
    ) {
      return {
        step: 'verify',
        email: parsed.email,
        firstName: parsed.firstName,
        lastName: parsed.lastName,
        sport: parsed.sport,
        programName: parsed.programName,
        acceptedTerms: true,
      };
    }
  } catch {
    // Malformed: fall through and drop it.
  }

  clearTravelBallProgramSignup();
  return null;
};
